import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import React, { ElementType, Fragment, PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { ScrollablePanelContext, ScrollablePanelContextType } from './context';

type Props = {
    isOpen?: boolean;
    onClose(): void;
    as?: ElementType;
};

export function ScrollablePanel({ isOpen, onClose, children, as }: PropsWithChildren<Props>) {
    const [didScrollBottom, setDidScrollBottom] = useState(false);

    const checkScrollIsBottom = useCallback((element: HTMLDivElement) => {
        requestAnimationFrame(() => {
            const { height } = element.getBoundingClientRect();
            if (element.scrollHeight <= element.scrollTop + height + 20) {
                setDidScrollBottom(true);
            }
        });
    }, []);

    const onScroll: React.UIEventHandler<HTMLDivElement> = useCallback(
        (event) => checkScrollIsBottom(event.target as HTMLDivElement),
        [checkScrollIsBottom]
    );

    const value: ScrollablePanelContextType = useMemo(
        () => ({
            didScrollBottom,
            onClose,
        }),
        [didScrollBottom, onClose]
    );

    return (
        <ScrollablePanelContext.Provider value={value}>
            <Transition show={isOpen} as={Fragment}>
                <Dialog
                    className="fixed inset-0 z-20 flex h-screen w-screen items-end justify-center pt-16 sm:items-center sm:p-8"
                    onClose={onClose}
                >
                    <TransitionChild
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/30" />
                    </TransitionChild>
                    <TransitionChild
                        as={Fragment}
                        enter="transition-all ease-out duration-300"
                        enterFrom="invisible opacity-0 translate-y-4"
                        enterTo="visible opacity-100 translate-y-0"
                        leave="transition-all ease-in duration-200"
                        leaveFrom="visible opacity-100 translate-y-0"
                        leaveTo="invisible opacity-0 translate-y-4"
                    >
                        <DialogPanel
                            className="relative mx-auto max-h-full max-w-xl overflow-auto rounded-xl border-gray-200 bg-white text-left shadow-xl sm:border"
                            onScroll={onScroll}
                            ref={(el: HTMLDivElement | null) => el && checkScrollIsBottom(el)}
                            as={as}
                        >
                            {children}
                        </DialogPanel>
                    </TransitionChild>
                </Dialog>
            </Transition>
        </ScrollablePanelContext.Provider>
    );
}
